/**
 * Overview: the three weakest capabilities (hotspot map, default key
 * and window), each one tap from its drill-down.
 */
import { Link } from "react-router-dom";
import { Kicker } from "@/components/ui/kicker";
import { PaperCard } from "@/components/ui/paper-card.js";
import { Chip } from "@/components/ui/chip";
import { cn } from "@/lib/utils";
import { STALE_TIMES } from "@/lib/query-performance";
import { trpc } from "@/lib/trpc";
import { useProjectAccess } from "@/lib/project-access";
import { hotspotHref } from "./hotspot-url-state";
import { ArrowDownRight, ArrowUpRight, AlertTriangle } from "lucide-react";

export function WeakestAreasCard({
  projectId,
  className,
}: {
  projectId: string;
  className?: string;
}) {
  const { data } = trpc.hotspot.list.useQuery(
    { projectId },
    { staleTime: STALE_TIMES.list },
  );
  const { isViewer } = useProjectAccess();
  if (!data) return null;
  // Only areas with unresolved problems (open or awaiting re-run) are
  // weak; clear and not-walked areas never make the list.
  const walked = data.rows.some((r) => r.status !== "not_walked");
  const top = data.rows.filter((r) => r.status === "measured").slice(0, 3);

  return (
    <section
      aria-labelledby="weakest-areas"
      className={cn("flex flex-col h-full", className)}
    >
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <Kicker as="h2">
          <span id="weakest-areas">Weakest areas</span>
        </Kicker>
        {walked && (
          <Link
            to={hotspotHref(projectId)}
            className="text-sm text-foreground underline underline-offset-4"
          >
            See all hotspots
          </Link>
        )}
      </div>
      <PaperCard as="article" padding="sm" className="flex-1 overflow-hidden">
        {top.length === 0 && walked ? (
          <p className="text-base text-foreground">
            No area has unresolved problems in this window.
          </p>
        ) : top.length === 0 ? (
          isViewer ? (
            <p className="text-base text-foreground">
              Hotspots appear after the first walkthrough. Ask an editor to run
              one.
            </p>
          ) : (
            <p className="text-base text-foreground">
              Hotspots appear after walkthroughs are evaluated against route
              patterns. Execute{" "}
              <code className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-sm">
                /iamu:run
              </code>{" "}
              from your agent or launch a walkthrough from a scenario.
            </p>
          )
        ) : (
          <ol className="divide-y divide-border/60">
            {top.map((row) => {
              const hasRegressions =
                row.regressed > 0 || (row.deltas?.regressed ?? 0) > 0;
              const hasOpen = row.openProblems > 0;
              const hasAwaiting = row.awaitingRerun > 0;
              const isBetter = row.direction === "better";
              const isWorse = row.direction === "worse";

              return (
                <li
                  key={row.key}
                  className="flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={hotspotHref(projectId, { key: row.key })}
                      className="text-sm font-semibold text-foreground underline-offset-4 hover:underline"
                    >
                      {row.label}
                    </Link>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {hasRegressions ? (
                        <Chip tone="critical">
                          <AlertTriangle
                            className="size-3"
                            aria-hidden="true"
                          />
                          Regressed
                        </Chip>
                      ) : hasOpen ? (
                        <Chip tone="important">{row.openProblems} open</Chip>
                      ) : hasAwaiting ? (
                        <Chip tone="outline">
                          {row.awaitingRerun} awaiting re-run
                        </Chip>
                      ) : null}

                      {isWorse && (
                        <Chip tone="critical">
                          <ArrowUpRight className="size-3" aria-hidden="true" />
                          Worse
                        </Chip>
                      )}
                      {isBetter && (
                        <Chip tone="helps">
                          <ArrowDownRight
                            className="size-3"
                            aria-hidden="true"
                          />
                          Improving
                        </Chip>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
                    {hasOpen && (
                      <span className="font-medium text-foreground">
                        {row.openProblems}{" "}
                        {row.openProblems === 1 ? "problem" : "problems"}{" "}
                        unresolved
                      </span>
                    )}
                    {!hasOpen && hasAwaiting && (
                      <span className="font-medium text-foreground">
                        {row.awaitingRerun}{" "}
                        {row.awaitingRerun === 1 ? "fix" : "fixes"} awaiting
                        re-run
                      </span>
                    )}
                    {row.deltas && row.deltas.new > 0 && (
                      <>
                        <span>·</span>
                        <span className="font-medium text-severity-important">
                          +{row.deltas.new} new
                        </span>
                      </>
                    )}
                    {row.deltas && row.deltas.fixed > 0 && (
                      <>
                        <span>·</span>
                        <span className="font-medium text-finding-helps">
                          -{row.deltas.fixed} fixed
                        </span>
                      </>
                    )}
                    {row.outcome && row.outcome.fail > 0 && (
                      <>
                        <span>·</span>
                        <span className="text-severity-critical">
                          {row.outcome.fail} human{" "}
                          {row.outcome.fail === 1 ? "test" : "tests"} failing
                        </span>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </PaperCard>
    </section>
  );
}
